import React, { useState, useEffect } from "react";
import "./Login.css";
import { useNavigate, NavLink } from "react-router-dom";
import { useForm, SubmitHandler } from "react-hook-form";
import axios from "axios";
import { Eye, EyeOff, Heart, Menu, X } from "lucide-react";
import { trackAuth, trackButtonClick } from "../../Components/Utils/analytics";
import { webPushManager } from "../../Components/Utils/webPush";
import { ayreneService, getTokenExpiration } from "../../services/ayrene.service";
import image21 from '../img/img-21.jpg'
import fervoirLogo from '../img/Fervoir-Logo.png';
import Footer from "../Footer/Footer";
import MobileLogin from "./MobileLogin";

// Define the form input types
interface LoginFormInputs {
  Email: string;
  Password: string;
}

const Login: React.FC = () => {
  const navigateTo = useNavigate();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormInputs>();
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrollOpacity, setScrollOpacity] = useState(1);
  const [isMobileView, setIsMobileView] = useState(false);
  const [showMobileLogin, setShowMobileLogin] = useState(false);
  const [showWelcomeScreen, setShowWelcomeScreen] = useState(true);

  useEffect(() => {
    const checkMobileView = () => {
      setIsMobileView(window.innerWidth <= 1024);
    };
    checkMobileView();
    window.addEventListener('resize', checkMobileView);
    return () => window.removeEventListener('resize', checkMobileView);
  }, []);

  const handleAyreneLogin = async (email: string, password: string) => {
    try {
      console.log('🚀 Starting Ayrene login integration...');

      const ayreneResult = await ayreneService.login({
        email,
        password
      });

      if (ayreneResult.success) {
        console.log('✅ Ayrene login integration successful!');
        if (ayreneResult.token) {
          localStorage.setItem('ayreneAuthToken', ayreneResult.token);
          const expiration = getTokenExpiration(ayreneResult.token);
          if (expiration) {
            localStorage.setItem('ayreneTokenExpiration', expiration.toString());
            console.log('💾 Token expiration stored:', new Date(expiration).toLocaleString());
          }
          console.log('💾 Ayrene access token stored in localStorage');
          console.log('🔍 Token verification:', ayreneResult.token.substring(0, 20) + '...');
        }
        if (ayreneResult.refreshToken) {
          localStorage.setItem('ayreneRefreshToken', ayreneResult.refreshToken);
          console.log('💾 Ayrene refresh token stored in localStorage');
        }
        localStorage.setItem('ayreneLoginStatus', 'success');
        console.log('📊 Ayrene login status: SUCCESS');
      } else {
        console.warn('⚠️ Ayrene login failed but continuing with Fervoir login');
        console.warn('📝 Ayrene error message:', ayreneResult.message);
        localStorage.setItem('ayreneLoginStatus', 'failed');
      }
    } catch (error) {
      console.error('❌ Ayrene login integration error (non-blocking):', error);
      localStorage.setItem('ayreneLoginStatus', 'error');
    }
  };

  const handleMobileLogin = async (email: string, password: string) => {
    setLoading(true);
    try {
      const response = await axios.post("https://nrimarriage.in/api/v1/users/Login", {
        email,
        password,
      });

      if (response.status === 200 && response.data.token) {
        const { token, userId } = response.data;
        localStorage.setItem("authToken", token);
        localStorage.setItem("userId", userId);

        await handleAyreneLogin(email, password);

        try {
          if (webPushManager.isSupported()) {
            setTimeout(async () => {
              try {
                await webPushManager.setupPushNotifications();
                console.log("✅ Web Push notifications setup completed");
              } catch (pushError) {
                console.error("Web Push failed (non-blocking):", pushError);
              }
            }, 2000);
          }
        } catch (pushError) {
          console.error("Web Push initialization failed:", pushError);
        }

        await checkProfileImage(token);
        trackAuth('login');
      } else {
        alert("Login failed: Invalid response");
      }
    } catch (err: any) {
      console.error("Login error:", err);
      alert(err.response?.data?.message || "An error occurred during login.");
    } finally {
      setLoading(false);
    }
  };

 const handleLogin: SubmitHandler<LoginFormInputs> = async (data) => {
  setLoading(true);

  try {
    const response = await axios.post("https://nrimarriage.in/api/v1/users/Login", {
      email: data.Email,
      password: data.Password,
    });

    if (response.status === 200 && response.data.token) {
      const { token, userId } = response.data;

      localStorage.setItem("authToken", token);
      localStorage.setItem("userId", userId);

      console.log("User login successful. UserId:", userId);

      await handleAyreneLogin(data.Email, data.Password);

      try {
        if (webPushManager.isSupported()) {
          setTimeout(async () => {
            try {
              await webPushManager.setupPushNotifications();
              console.log("✅ Web Push notifications setup completed");
            } catch (pushError) {
              console.error("Web Push: setupPushNotifications failed (non-blocking):", pushError);
            }
          }, 2000);
        } else {
          console.warn("Web Push: Browser not supported for push notifications.");
        }
      } catch (pushError) {
        console.error("Web Push: Web Push initialization failed (non-blocking):", pushError);
      }

      await checkProfileImage(token);
      trackAuth('login');
    } else {
      alert("Login failed: Invalid response");
    }
  } catch (err: any) {
    console.error("Login error:", err);
    alert(err.response?.data?.message || "An error occurred during login.");
  } finally {
    setLoading(false);
  }
};

  /**
   * Check if user has a profile image
   */
  const checkProfileImage = async (token: string) => {
    try {
      const profileRes = await axios.get("https://nrimarriage.in/api/v1/users/Profile", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      console.log("Full profile response:", profileRes.data);

      const profileImage: string = profileRes.data.profilePictureUrl;

      if (profileImage && profileImage.trim() !== "") {
        navigateTo("/home");
      } else {
        navigateTo("/upload-profile-image");
      }
    } catch (err) {
      console.error("Failed to fetch profile:", err);
      alert("Failed to fetch user profile.");
    }
  };

  if (isMobileView && showMobileLogin) {
    return (
      <MobileLogin
        onSubmit={handleMobileLogin}
        onClose={() => {
          setShowMobileLogin(false);
          setShowWelcomeScreen(true);
        }}
        loading={loading}
      />
    );
  }

  if (isMobileView && showWelcomeScreen) {
    return (
      <div className="login-container">
        <div className="floating-shapes1">
          <Heart className="shape1 heart1" size={40} />
          <Heart className="shape1 heart1-2" size={32} />
          <Heart className="shape1 heart1-3" size={36} />
        </div>

        <header className="login-header">
          <div className="header-content1">
            <div className="logo-section1">
              <h1 className="logo">
                <img src={fervoirLogo} alt="Fervoir logo" className="fervoir-logo-img1" />
                Fervoir
              </h1>
            </div>

            <nav className={`nav-menu1 ${mobileMenuOpen ? 'mobile-open' : ''}`}>
              <a href="#products" className="nav-link3">Products</a>
              <a href="#learn" className="nav-link3">Learn</a>
              <a href="#safety" className="nav-link3">Safety</a>
              <a href="#support" className="nav-link3">Support</a>
              <a href="#download" className="nav-link3">Download</a>
            </nav>

            <div className="header-actions1">
              <button className="register-header-btn1" onClick={() => navigateTo("/registration")}>
                Register
              </button>
              <button className="mobile-menu-toggle1" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
                {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </header>

        <main className="welcome-screen-main1">
          <div className="welcome-content1">
            <div className="welcome-hero1">
              <h1 className="welcome-title1">Welcome Back!</h1>
              <p className="welcome-subtitle1">Continue your journey to find meaningful connections</p>
              <p className="welcome-description1">
                Log in to access your profile, view matches, and connect with people who share your interests.
              </p>
            </div>

            <button
              className="get-started-btn1"
              onClick={() => {
                setShowWelcomeScreen(false);
                setShowMobileLogin(true);
              }}
            >
              Log In
            </button>

            <div className="welcome-features1">
              <div className="feature-item1">
                <div className="feature-icon1">💝</div>
                <p>Your Matches</p>
              </div>
              <div className="feature-item1">
                <div className="feature-icon1">💬</div>
                <p>Messages</p>
              </div>
              <div className="feature-item1">
                <div className="feature-icon1">⚡</div>
                <p>Quick Access</p>
              </div>
            </div>

            <div className="welcome-footer-text1">
              <span>Don't have an account? </span>
              <button
                className="welcome-register-link1"
                onClick={() => navigateTo('/registration')}
              >
                Sign up
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="floating-shapes1">
        <Heart className="shape1 heart1" size={40} />
        <Heart className="shape1 heart1-2" size={32} />
        <Heart className="shape1 heart1-3" size={36} />
      </div>

      <header className="login-header">
        <div className="header-content1">
          <div className="logo-section1">
            <h1 className="logo">
              <img src={fervoirLogo} alt="Fervoir logo" className="fervoir-logo-img1" />
              Fervoir
            </h1>
          </div>

          <nav className={`nav-menu1 ${mobileMenuOpen ? 'mobile-open' : ''}`}>
            <a href="#products" className="nav-link3">Products</a>
            <a href="#learn" className="nav-link3">Learn</a>
            <a href="#safety" className="nav-link3">Safety</a>
            <a href="#support" className="nav-link3">Support</a>
            <a href="#download" className="nav-link3">Download</a>
          </nav>

          <div className="header-actions1">
            <button className="register-header-btn1" onClick={() => navigateTo("/registration")}>
              Register
            </button>
            <button className="mobile-menu-toggle1" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </header>

      <main className="login-main">
        <div className="hero-section1">
          <div className="hero-content1">
            <h1>Find Your Perfect Match</h1>
            <p>Begin your journey to forever with our sophisticated matchmaking platform.</p>
            <div className="hero-image1">
             <img src={image21} alt="Couple" />
              <div className="image-overlay1"></div>
            </div>
          </div>
        </div>

        <div className="login-form-container">
          <form onSubmit={handleSubmit(handleLogin)} className="login-form">
            <h2>Login to Your Account</h2>
            <p className="form-subtitle1">Access your personalized matchmaking experience</p>

            <div className="form-group101">
              <input
                type="email"
                placeholder="Email Address"
                {...register("Email", { required: "Email is required" })}
              />
              {errors.Email && <span className="error">{errors.Email.message}</span>}
            </div>

            <div className="form-group101 password-group1">
              <div className="password-input1">
                <input
                  type={passwordVisible ? "text" : "password"}
                  placeholder="Password"
                  {...register("Password", { required: "Password is required" })}
                />
                <button
                  type="button"
                  className="toggle-password1"
                  onClick={() => setPasswordVisible(!passwordVisible)}
                >
                  {passwordVisible ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              {errors.Password && <span className="error">{errors.Password.message}</span>}
            </div>

            <button type="submit" className="submit-btn1" disabled={loading}>
              {loading ? <span className="spinner1"></span> : "Login"}
            </button>

            <div className="form-footer1">
              <NavLink to="/forgotpassword" className="forgot-password-link">
                Forgot Password?
              </NavLink>
              <NavLink to="/registration" className="login-link1">
                Not registered? Sign up now
              </NavLink>
            </div>
          </form>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Login;
